import { useEffect, useState, useRef, useCallback } from 'react';
import { AlertTriangle, X, Navigation } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { io, Socket } from 'socket.io-client';
import { useAppDispatch, useAppSelector } from '../../store';
import { setEmergencyLocation } from '../../store/slices/uiSlice';
import { useActiveEvent } from '../../contexts/EventContext';
import { API_BASE_URL, SOCKET_URL } from '../../lib/utils';
import {
  type ActiveEmergency,
  clearEmergencyLocationStorage,
  persistEmergencyLocation,
  readEmergencyLocation,
} from '../../lib/emergencyLocation';
import type { Incident } from '../../types';

interface EmergencyPayload {
  incidentId: string;
  eventId: string;
  resourceId: string;
  resourceName: string;
  location: { latitude: number; longitude: number; address: string };
}

function isOpenEmergencyIncident(incident: Incident): boolean {
  return incident.title?.startsWith('EMERGENCY') === true && incident.status === 'open';
}

function toEmergencyPayload(incident: Incident): EmergencyPayload {
  const titleMatch = incident.title.match(/EMERGENCY\s*-\s*(\S+)/);
  const resourceName = titleMatch ? titleMatch[1] : 'Unknown';
  return {
    incidentId: incident.id,
    eventId: incident.eventId,
    resourceId: resourceName,
    resourceName,
    location: incident.location,
  };
}

export default function EmergencyAlert() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { token } = useAppSelector((state) => state.auth);
  const storedEmergency = useAppSelector((state) => state.ui.emergencyLocation);
  const { activeEventId } = useActiveEvent();
  const [emergency, setEmergency] = useState<EmergencyPayload | null>(null);
  const [visible, setVisible] = useState(false);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const initializedRef = useRef(false);

  const clearEmergency = useCallback(() => {
    dispatch(setEmergencyLocation(null));
    clearEmergencyLocationStorage();
    setVisible(false);
    setEmergency(null);
  }, [dispatch]);

  const activateEmergency = useCallback(
    (payload: EmergencyPayload, playSound = true) => {
      const active: ActiveEmergency = {
        latitude: payload.location.latitude,
        longitude: payload.location.longitude,
        eventId: payload.eventId,
        incidentId: payload.incidentId,
      };
      dispatch(setEmergencyLocation(active));
      persistEmergencyLocation(active);
      setEmergency(payload);
      setVisible(true);
      if (playSound) playEmergencyTone();
    },
    [dispatch]
  );

  const syncEmergencies = useCallback(async () => {
    if (!token || !activeEventId) {
      clearEmergency();
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE_URL}/incidents?eventId=${encodeURIComponent(activeEventId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) return;
      const data = await res.json();
      const incidents: Incident[] = data.incidents || [];
      const openEmergencies = incidents.filter(isOpenEmergencyIncident);

      if (storedEmergency?.incidentId) {
        const stillOpen = openEmergencies.some((i) => i.id === storedEmergency.incidentId);
        if (!stillOpen) {
          clearEmergency();
          return;
        }
      }

      if (!initializedRef.current) {
        openEmergencies.forEach((e) => seenIdsRef.current.add(e.id));
        initializedRef.current = true;

        const persisted = readEmergencyLocation();
        if (
          persisted &&
          persisted.eventId === activeEventId &&
          openEmergencies.some((i) => i.id === persisted.incidentId)
        ) {
          dispatch(
            setEmergencyLocation({
              latitude: persisted.latitude,
              longitude: persisted.longitude,
              eventId: persisted.eventId,
              incidentId: persisted.incidentId,
            })
          );
        }
        return;
      }

      const newEmergencies = openEmergencies.filter((e) => !seenIdsRef.current.has(e.id));
      newEmergencies.forEach((e) => seenIdsRef.current.add(e.id));

      if (newEmergencies.length > 0) {
        activateEmergency(toEmergencyPayload(newEmergencies[0]));
      }
    } catch {
      /* ignore */
    }
  }, [token, activeEventId, storedEmergency?.incidentId, clearEmergency, dispatch, activateEmergency]);

  useEffect(() => {
    initializedRef.current = false;
    seenIdsRef.current.clear();
  }, [activeEventId]);

  useEffect(() => {
    syncEmergencies();
    const interval = setInterval(syncEmergencies, 5000);
    return () => clearInterval(interval);
  }, [syncEmergencies]);

  useEffect(() => {
    if (!token) return;

    const socket: Socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    const onIncidentUpdated = (incident: Incident) => {
      if (!storedEmergency || incident.id !== storedEmergency.incidentId) return;
      if (!isOpenEmergencyIncident(incident)) {
        clearEmergency();
      }
    };

    socket.on('incident:updated', onIncidentUpdated);
    return () => {
      socket.off('incident:updated', onIncidentUpdated);
      socket.disconnect();
    };
  }, [token, storedEmergency?.incidentId, clearEmergency]);

  const playEmergencyTone = () => {
    try {
      const ctx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();

      const playWhoop = (startTime: number, lowFreq: number, highFreq: number, duration: number) => {
        const osc1 = ctx.createOscillator();
        osc1.type = 'sawtooth';
        const gain1 = ctx.createGain();
        osc1.connect(gain1);
        gain1.connect(ctx.destination);

        const osc2 = ctx.createOscillator();
        osc2.type = 'square';
        const gain2 = ctx.createGain();
        osc2.connect(gain2);
        gain2.connect(ctx.destination);

        osc1.frequency.setValueAtTime(lowFreq, startTime);
        osc1.frequency.linearRampToValueAtTime(highFreq, startTime + duration * 0.5);
        osc1.frequency.linearRampToValueAtTime(lowFreq, startTime + duration);

        osc2.frequency.setValueAtTime(lowFreq * 1.5, startTime);
        osc2.frequency.linearRampToValueAtTime(highFreq * 1.5, startTime + duration * 0.5);
        osc2.frequency.linearRampToValueAtTime(lowFreq * 1.5, startTime + duration);

        gain1.gain.setValueAtTime(0, startTime);
        gain1.gain.linearRampToValueAtTime(0.25, startTime + 0.02);
        gain1.gain.setValueAtTime(0.25, startTime + duration - 0.05);
        gain1.gain.linearRampToValueAtTime(0, startTime + duration);

        gain2.gain.setValueAtTime(0, startTime);
        gain2.gain.linearRampToValueAtTime(0.08, startTime + 0.02);
        gain2.gain.setValueAtTime(0.08, startTime + duration - 0.05);
        gain2.gain.linearRampToValueAtTime(0, startTime + duration);

        osc1.start(startTime);
        osc1.stop(startTime + duration);
        osc2.start(startTime);
        osc2.stop(startTime + duration);
      };

      const now = ctx.currentTime;
      const whoopDuration = 0.5;
      const gap = 0.08;

      for (let i = 0; i < 4; i++) {
        const t = now + i * (whoopDuration + gap);
        playWhoop(t, 330, 660, whoopDuration);
      }

      setTimeout(() => {
        try {
          ctx.close();
        } catch {
          /* ignore */
        }
      }, (whoopDuration + gap) * 4 * 1000 + 500);
    } catch {
      /* Audio not available */
    }
  };

  const handleDismiss = () => {
    clearEmergency();
  };

  const handleViewIncident = () => {
    if (emergency) {
      navigate(`/workspace/incidents/${emergency.incidentId}`);
      setVisible(false);
      setTimeout(() => setEmergency(null), 300);
    }
  };

  if (!visible || !emergency) return null;

  return (
    <div className="fixed inset-0 z-[99999] flex items-center justify-center bg-red-500/95 backdrop-blur-md animate-in fade-in duration-300">
      <div className="max-w-md w-full mx-4">
        <div className="bg-card border-2 border-red-500 rounded-2xl overflow-hidden shadow-2xl">
          <div className="bg-red-500 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-white animate-pulse" />
              <div>
                <h2 className="text-xl font-extrabold text-white">EMERGENCY ACTIVATED</h2>
                <p className="text-red-100 text-sm">Urgent assistance requested</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="w-8 h-8 rounded-lg bg-red-400/20 hover:bg-red-400/30 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3 p-4 rounded-xl bg-red-500/10 border border-red-500/20">
              <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
                <Navigation className="w-6 h-6 text-red-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white">{emergency.resourceName}</p>
                <p className="text-xs text-white/60 truncate">{emergency.location.address}</p>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-white/80">
                Incident <span className="font-mono font-bold text-white">{emergency.incidentId}</span> has been created
              </p>
              <p className="text-xs text-white/50 mt-1">Resource has been automatically assigned</p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleDismiss}
                className="flex-1 py-3 rounded-xl bg-white/10 border border-white/10 text-white font-bold text-sm hover:bg-white/15 transition-colors"
              >
                Dismiss
              </button>
              <button
                onClick={handleViewIncident}
                className="flex-1 py-3 rounded-xl bg-red-500 text-white font-bold text-sm hover:bg-red-400 transition-colors flex items-center justify-center gap-2"
              >
                <Navigation className="w-4 h-4" />
                View Incident
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
